/**
 * MemberPortal - Modern Cathedral Design
 * Configurable dashboard with Bible, Events, Giving, Prayer, etc.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';

// Icons
import {
  BookOpen, Calendar, Heart, Bell, CreditCard, Users, MapPin,
  ChevronRight, Sparkles, LogOut, Settings, Home, Menu, X,
  MessageSquare, TrendingUp, Flame, Star
} from 'lucide-react';

// Bible Dashboard (imported)
import BibleDashboard from '@/components/bible/BibleDashboard';
import AssignedPlansSection from '@/components/bible/AssignedPlansSection';
import DirectoryConsentCard from '@/components/member/DirectoryConsentCard';
import { MotherGeneralTributes } from '@/components/church/MotherGeneralTributes';

// ============== Quick Action Card ==============
const QuickActionCard = ({ icon: Icon, title, description, onClick, color, badge }) => (
  <Card 
    className="group cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden"
    onClick={onClick}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className={cn(
          "w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg transition-transform group-hover:scale-110",
          color
        )}>
          <Icon className="h-6 w-6" />
        </div>
        {badge && (
          <Badge variant="secondary" className="text-xs">{badge}</Badge>
        )}
      </div>
      <h3 className="font-semibold text-slate-900 mt-4 group-hover:text-indigo-600 transition-colors">
        {title}
      </h3>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
      <ChevronRight className="h-5 w-5 text-slate-300 mt-3 group-hover:text-indigo-500 group-hover:translate-x-1 transition-all" />
    </CardContent>
  </Card>
);

// ============== Verse of the Day Widget ==============
const VerseWidget = () => {
  const { data: verse } = useQuery({
    queryKey: ['verse-of-day'],
    queryFn: () => api.get('/bible/verse-of-the-day').then(r => r.data),
  });

  if (!verse) return null;

  return (
    <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0 shadow-xl overflow-hidden relative">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-40 h-40 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-500/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-2xl" />
      
      <CardContent className="p-6 relative">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-gold" />
          <span className="text-sm font-medium text-gold">Verse of the Day</span>
        </div>
        
        <blockquote className="font-bible text-xl leading-relaxed mb-4 text-white/90">
          "{verse.text}"
        </blockquote>
        
        <cite className="text-white/60 text-sm">
          — {verse.book} {verse.chapter}:{verse.verse}
        </cite>
      </CardContent>
    </Card>
  );
};

// ============== Announcements Widget ==============
const AnnouncementsWidget = ({ announcements }) => {
  if (!announcements || announcements.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-500" />
          Announcements
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {announcements.slice(0, 3).map((ann, idx) => (
          <div 
            key={idx}
            className={cn(
              "p-3 rounded-lg border-l-4",
              ann.priority === 'high' 
                ? "bg-red-50 border-red-500" 
                : "bg-slate-50 border-indigo-500"
            )}
          >
            <h4 className="font-medium text-slate-900 text-sm">{ann.title}</h4>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{ann.content}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ============== Stats Widget ==============
const StatsWidget = ({ stats }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
    <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-100">
      <CardContent className="p-4 text-center">
        <Flame className="h-6 w-6 text-orange-500 mx-auto mb-2" />
        <div className="text-2xl font-bold text-slate-900">{stats?.streak || 0}</div>
        <div className="text-xs text-slate-500">Day Streak</div>
      </CardContent>
    </Card>
    <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
      <CardContent className="p-4 text-center">
        <BookOpen className="h-6 w-6 text-indigo-500 mx-auto mb-2" />
        <div className="text-2xl font-bold text-slate-900">{stats?.chapters_read || 0}</div>
        <div className="text-xs text-slate-500">Chapters Read</div>
      </CardContent>
    </Card>
    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100">
      <CardContent className="p-4 text-center">
        <Heart className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
        <div className="text-2xl font-bold text-slate-900">{stats?.prayers || 0}</div>
        <div className="text-xs text-slate-500">Prayers</div>
      </CardContent>
    </Card>
    <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-pink-100">
      <CardContent className="p-4 text-center">
        <Star className="h-6 w-6 text-pink-500 mx-auto mb-2" />
        <div className="text-2xl font-bold text-slate-900">{stats?.badges || 0}</div>
        <div className="text-xs text-slate-500">Badges</div>
      </CardContent>
    </Card>
  </div>
);

// ============== Sidebar ==============
const Sidebar = ({ isOpen, onClose, user, onLogout }) => {
  const navigate = useNavigate();
  
  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/member' },
    { icon: BookOpen, label: 'Bible', path: '/bible' },
    { icon: Calendar, label: 'Reading Plans', path: '/bible' },
    { icon: Heart, label: 'Prayer Journal', path: '/member/prayers' },
    { icon: CreditCard, label: 'Giving', path: '/member/giving' },
    { icon: Calendar, label: 'Events', path: '/events' },
    { icon: MapPin, label: 'Churches', path: '/churches' },
    { icon: Settings, label: 'Settings', path: '/member/settings' },
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 h-full w-72 bg-white border-r z-50 transform transition-transform lg:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-gold" />
              </div>
              <span className="font-heading font-bold text-slate-900">FaithHub</span>
            </div>
            <button onClick={onClose} className="lg:hidden">
              <X className="h-5 w-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* User Info */}
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-indigo-100 text-indigo-600">
                {user?.name?.charAt(0) || 'M'}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="font-medium text-slate-900">{user?.name || 'Member'}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>
        </div>

        {/* Menu */}
        <ScrollArea className="flex-1 p-4">
          <nav className="space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  onClose();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-4 border-t">
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

// ============== Main Component ==============
const MemberPortal = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Fetch portal settings
  const { data: settings } = useQuery({
    queryKey: ['portal-settings'],
    queryFn: () => api.get('/admin/portal-settings').then(r => r.data?.member || {}).catch(() => ({})),
  });

  // Their own church — name, logo and accent colour for the greeting.
  const { data: me } = useQuery({
    queryKey: ['member-me'],
    queryFn: () => api.get('/member/me').then(r => r.data).catch(() => null),
  });
  const church = me?.church || null;
  const accent = church?.accent_color || '#4f46e5';

  // Fetch announcements
  const { data: announcements = [] } = useQuery({
    queryKey: ['member-announcements'],
    queryFn: () => api.get('/member/announcements').then(r => r.data).catch(() => []),
  });

  // Fetch user stats
  const { data: stats } = useQuery({
    queryKey: ['member-stats'],
    queryFn: () => api.get('/member/stats').then(r => r.data).catch(() => ({})),
  });

  const handleLogout = () => {
    logout();
    navigate('/login?type=member');
  };

  // Check if module is enabled
  const isEnabled = (module) => settings?.modules?.[module] !== false;

  const quickActions = [
    { key: 'bible', icon: BookOpen, title: 'Read Bible', description: 'Continue your reading', color: 'bg-gradient-to-br from-indigo-500 to-purple-600', onClick: () => navigate('/bible') },
    { key: 'reading_plans', icon: Calendar, title: 'Reading Plans', description: 'Start a new plan', color: 'bg-gradient-to-br from-emerald-500 to-teal-600', onClick: () => navigate('/bible') },
    { key: 'prayer', icon: Heart, title: 'Prayer Journal', description: 'Add a prayer', color: 'bg-gradient-to-br from-pink-500 to-rose-600', onClick: () => navigate('/member/prayers') },
    { key: 'giving', icon: CreditCard, title: 'Give Online', description: 'Tithes & offerings', color: 'bg-gradient-to-br from-amber-500 to-orange-600', onClick: () => navigate('/member/giving') },
    { key: 'events', icon: Calendar, title: 'Events', description: 'Upcoming events', color: 'bg-gradient-to-br from-blue-500 to-cyan-600', onClick: () => navigate('/events') },
    { key: 'directory', icon: MapPin, title: 'Find Church', description: 'Church directory', color: 'bg-gradient-to-br from-slate-600 to-slate-800', onClick: () => navigate('/churches') },
  ].filter(action => isEnabled(action.key));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        user={user}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <div className="lg:pl-72">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b px-4 sm:px-6 py-4"
                style={church ? { borderTopWidth: 3, borderTopColor: accent } : undefined}
                data-testid="member-portal-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              >
                <Menu className="h-5 w-5" />
              </button>
              {church?.logo_url && (
                <img src={church.logo_url} alt={church.name}
                     className="h-11 w-11 rounded-xl object-cover ring-2 ring-white shadow-sm hidden sm:block"
                     data-testid="member-church-logo"
                     onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-900">
                  Welcome back, {user?.name?.split(' ')[0] || 'Friend'}
                </h1>
                {church ? (
                  <p className="text-sm flex flex-wrap items-center gap-1.5" data-testid="member-church-greeting">
                    <span className="font-medium" style={{ color: accent }}>{church.name}</span>
                    {(church.city || church.country) && (
                      <span className="text-slate-400">· {[church.city, church.country].filter(Boolean).join(', ')}</span>
                    )}
                    {church.slug && (
                      <Link to={`/churches/${church.slug}`}
                            className="text-xs text-slate-400 hover:underline"
                            data-testid="member-church-link">Visit church page</Link>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">Let's continue your spiritual journey</p>
                )}
              </div>
            </div>
            <Avatar className="h-10 w-10">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback style={church ? { backgroundColor: `${accent}1a`, color: accent } : undefined}
                              className={church ? '' : 'bg-indigo-100 text-indigo-600'}>
                {user?.name?.charAt(0) || 'M'}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="p-4 sm:p-6 max-w-6xl mx-auto space-y-8">
          {/* Verse of the Day */}
          {isEnabled('bible') && settings?.appearance?.showVerse !== false && (
            <VerseWidget />
          )}

          {/* Stats */}
          <StatsWidget stats={stats} />

          {/* Quick Actions Grid */}
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickActions.map((action) => (
                <QuickActionCard
                  key={action.key}
                  icon={action.icon}
                  title={action.title}
                  description={action.description}
                  color={action.color}
                  onClick={action.onClick}
                  badge={action.badge}
                />
              ))}
            </div>
          </div>

          {/* Announcements */}
          {isEnabled('announcements') && (
            <AnnouncementsWidget announcements={announcements} />
          )}

          {/* Consent to be found by church admins — iter 584 */}
          <DirectoryConsentCard />

          {/* Plans set by their church, province or an event — iter 582 */}
          {isEnabled('bible') && (
            <AssignedPlansSection onViewPlan={(userPlanId) => navigate('/bible', { state: { planId: userPlanId } })} />
          )}

          {/* Bible Section (Embedded) */}
          {isEnabled('bible') && (
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Continue Reading</h2>
              <BibleDashboard embedded={true} />
            </div>
          )}
        </main>

        {/* The tributes wall every assembly carries — iter 704 */}
        <MotherGeneralTributes />
      </div>
    </div>
  );
};

export default MemberPortal;
